// BFCM: hides selling plans (one list while a promo phase is active, another the rest of the time),
// where the page is built in the browser:
// - Rebuy widgets and the Rebuy Smart Cart: hidden plans are removed from the product data, so they
//   can't be shown, selected or priced
// - Cart: lines that already have a hidden plan are moved to the variant's first plan that is shown,
//   or to one-time purchase when every plan is hidden
// Rules and dates come from snippets/bfcm-selling-plans-config.liquid (Theme settings > BFCM),
// matched the same way as snippets/bfcm-hidden-selling-plans.liquid: plan name, group name or percentage discount.
(() => {
    const configEl = document.getElementById('bfcm-selling-plans-config');
    if (!configEl) return;

    let config;
    try {
        config = JSON.parse(configEl.textContent);
    } catch (e) {
        return;
    }

    // Unix seconds, 0 = no limit; checked in the browser so cached pages switch on time
    const isPromoActive = () => {
        const now = Date.now() / 1000;
        return (config.phases || []).some(({start, end}) => {
            if (!start && !end) return false;
            return !(start && now < start) && !(end && now > end);
        });
    };

    const rules = ((isPromoActive() ? config.rules : config.inactiveRules) || []).map(rule => String(rule).trim().toLowerCase()).filter(Boolean);
    if (!rules.length) return;

    const isHiddenPlan = (plan, group) => {
        const planName = String(plan.name || '').trim().toLowerCase();
        const groupName = String(group?.name || '').trim().toLowerCase();
        const adjustment = (plan.price_adjustments || [])[0];

        return rules.some(rule => {
            if (rule === planName || rule === groupName) return true;

            const value = rule.replace('%', '').trim();
            return value !== '' && !isNaN(value) && adjustment?.value_type === 'percentage' && Number(adjustment.value) === Number(value);
        });
    };

    // Names of hidden plans and their groups; Rebuy lists subscription frequencies by these names
    const hiddenNames = new Set(rules);

    const getHiddenIds = (product) => {
        const hiddenIds = new Set();
        (product?.selling_plan_groups || []).forEach(group => {
            (group.selling_plans || []).forEach(plan => {
                if (!isHiddenPlan(plan, group)) return;
                hiddenIds.add(String(plan.id));
                hiddenNames.add(String(plan.name || '').trim().toLowerCase());
                hiddenNames.add(String(group.name || '').trim().toLowerCase());
            });
        });
        return hiddenIds;
    };

    const isHiddenFrequency = (frequency) => hiddenNames.has(String(frequency).trim().toLowerCase());

    // Arrays are changed in place so Rebuy's Vue templates re-render
    const removeWhere = (list, test) => {
        for (let i = list.length - 1; i >= 0; i--) {
            if (test(list[i])) list.splice(i, 1);
        }
    };

    const filterProduct = (product) => {
        const hiddenIds = getHiddenIds(product);
        if (!hiddenIds.size) return;

        product.selling_plan_groups.forEach(group => removeWhere(group.selling_plans || [], plan => hiddenIds.has(String(plan.id))));
        removeWhere(product.selling_plan_groups, group => !(group.selling_plans || []).length);

        // Prices in the templates read selling_plan_allocations[0], so the first plan left is the one shown
        const variants = [...(product.variants || []), product.selected_variant].filter(Boolean);
        variants.forEach(variant => {
            if (Array.isArray(variant.selling_plan_allocations)) {
                removeWhere(variant.selling_plan_allocations, allocation => hiddenIds.has(String(allocation.selling_plan_id)));
            }
        });

        // Frequencies (widget and Smart Cart selects) are plan names, e.g. "1 month subscription"
        if (Array.isArray(product.subscription_frequencies)) {
            removeWhere(product.subscription_frequencies, isHiddenFrequency);
            if (product.subscription_frequency && isHiddenFrequency(product.subscription_frequency)) {
                product.subscription_frequency = product.subscription_frequencies[0];
            }
        }

        // No plan left: one-time purchase. Rebuy prices (variantPrice) and adds with its own
        // subscription_discount_amount while product.subscription is on, whatever the plans say
        if (!product.selling_plan_groups.length) {
            product.subscription = false;
            product.subscription_frequency = null;
            product.subscription_discount_type = null;
            product.subscription_discount_amount = null;
        }
    };

    // Rebuy widgets: same hooks as assets/rebuy-oos-variant.js (widget data arrives in event.detail.widget);
    // rebuy.ready also covers widgets that loaded before this script
    // widget.data.products is Rebuy's copy; the rendered (reactive) copy is on the widget's Vue instance
    const filterWidget = (widget) => {
        (widget?.data?.products || []).forEach(filterProduct);
        (widget?.View?.vue?.products || []).forEach(filterProduct);
    };
    ['rebuy.beforeProductsChange', 'rebuy.productsChange'].forEach(name => {
        document.addEventListener(name, (event) => filterWidget(event.detail?.widget || event.detail));
    });
    document.addEventListener('rebuy.ready', () => Object.values(window.Rebuy?.widgets || {}).forEach(filterWidget));
    Object.values(window.Rebuy?.widgets || {}).forEach(filterWidget);

    // Rebuy Smart Cart: its frequency select calls subscriptionFrequencies(item) on the cart's Vue instance,
    // which Rebuy fills from its own product cache, so the list is filtered there
    const patchSmartCart = () => {
        const vm = document.querySelector('.rebuy-cart')?.__vue__;
        if (!vm || vm.bfcmPatched || typeof vm.subscriptionFrequencies !== 'function') return Boolean(vm?.bfcmPatched);

        const subscriptionFrequencies = vm.subscriptionFrequencies;
        vm.subscriptionFrequencies = function (item, ...args) {
            const frequencies = subscriptionFrequencies.call(this, item, ...args);
            // Rebuy returns nothing for products it has no subscription data for; keep that as is
            if (!Array.isArray(frequencies)) return frequencies;
            if (item?.product) getHiddenIds(item.product);
            return frequencies.filter(frequency => !isHiddenFrequency(frequency));
        };
        vm.bfcmPatched = true;
        vm.$forceUpdate();
        return true;
    };

    const filterSmartCart = (cart) => {
        patchSmartCart();
        (cart?.items || []).forEach(item => item.product && filterProduct(item.product));
    };
    document.addEventListener('rebuy:smartcart.ready', (event) => filterSmartCart(event.detail?.smartcart?.cart));
    document.addEventListener('rebuy:smartcart.show', (event) => filterSmartCart(event.detail?.smartcart?.cart));
    document.addEventListener('rebuy:cart.change', (event) => {
        filterSmartCart(event.detail?.cart?.cart);
        fixCartLines();
    });

    // Smart Cart mounts after this script; patch it as soon as it exists (give up after 30s)
    let attempts = 0;
    const patchInterval = setInterval(() => {
        if (patchSmartCart() || ++attempts > 60) clearInterval(patchInterval);
    }, 500);

    // Cart lines added with a plan that is now hidden
    let fixing = false;
    const fixCartLines = async () => {
        if (fixing) return;
        fixing = true;

        try {
            const root = window.Shopify?.routes?.root || '/';
            const cart = await fetch(`${root}cart.js`).then(response => response.json());
            const products = {};
            let changed = false;

            // Lines are changed one at a time, by key, so line numbers don't shift
            for (const item of cart.items || []) {
                const planId = item.selling_plan_allocation?.selling_plan?.id;
                if (!planId) continue;

                if (!products[item.handle]) {
                    products[item.handle] = await fetch(`${root}products/${item.handle}.js`).then(response => response.json());
                }
                const product = products[item.handle];
                const hiddenIds = getHiddenIds(product);
                if (!hiddenIds.has(String(planId))) continue;

                const variant = (product.variants || []).find(v => v.id === item.variant_id);
                const allocation = (variant?.selling_plan_allocations || []).find(a => !hiddenIds.has(String(a.selling_plan_id)));
                // No plan left: one-time purchase (null removes the plan), unless the product can't be bought without one
                if (!allocation && product.requires_selling_plan) continue;

                await fetch(`${root}cart/change.js`, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({id: item.key, quantity: item.quantity, selling_plan: allocation ? allocation.selling_plan_id : null})
                });
                changed = true;
            }

            if (changed) {
                if (window.location.pathname.endsWith('/cart')) {
                    window.location.reload();
                } else {
                    window.Rebuy?.Cart?.fetchCart?.();
                }
            }
        } catch (e) {
            console.error('BFCM selling plans:', e);
        } finally {
            fixing = false;
        }
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', fixCartLines);
    } else {
        fixCartLines();
    }
})();
